import type {
    ColorThemeManifest,
} from "../contracts";
import {
    KNOWN_THEME_COLOR_VARIABLES,
    THEME_COLOR_TOKEN_TO_CSS_VARIABLE,
    type ThemeColorTokenId,
} from "../registry/color-registry";
import {
    KNOWN_SEMANTIC_TOKEN_VARIABLES,
    SEMANTIC_TOKEN_TO_CSS_VARIABLE,
    type SemanticTokenId,
} from "../registry/semantic-token-registry";
import {
    KNOWN_SYNTAX_TOKEN_VARIABLES,
    SYNTAX_TOKEN_TO_CSS_VARIABLE,
    type SyntaxTokenId,
} from "../registry/syntax-token-registry";
import { getColorThemeManifest } from "./theme-resolver";

export interface CompiledThemeArtifacts {
    manifest: ColorThemeManifest<ThemeColorTokenId, SyntaxTokenId, SemanticTokenId>;
    styleVariables: Record<string, string>;
}

export const KNOWN_THEME_STYLE_VARIABLES = [
    ...KNOWN_THEME_COLOR_VARIABLES,
    ...KNOWN_SYNTAX_TOKEN_VARIABLES,
    ...KNOWN_SEMANTIC_TOKEN_VARIABLES,
];

export function compileThemeStyleVariables(params: {
    themeId: string;
}): CompiledThemeArtifacts {
    const manifest = getColorThemeManifest(params.themeId);

    return {
        manifest,
        styleVariables: {
            ...compileTokenValues(manifest.colors, THEME_COLOR_TOKEN_TO_CSS_VARIABLE),
            ...compileTokenValues(manifest.syntaxTokens, SYNTAX_TOKEN_TO_CSS_VARIABLE),
            ...compileTokenValues(manifest.semanticTokens, SEMANTIC_TOKEN_TO_CSS_VARIABLE),
        },
    };
}

export function applyStyleVariables(
    root: HTMLElement,
    styleVariables: Record<string, string>,
    knownVariableNames: string[],
): void {
    for (const cssVariable of knownVariableNames) {
        const value = styleVariables[cssVariable];
        if (value) {
            root.style.setProperty(cssVariable, value);
        } else {
            root.style.removeProperty(cssVariable);
        }
    }
}

function compileTokenValues<TTokenId extends string>(
    values: Partial<Record<TTokenId, string>> | undefined,
    tokenToCssVariable: Record<TTokenId, string>,
): Record<string, string> {
    if (!values) return {};

    const compiled: Record<string, string> = {};
    for (const [tokenId, value] of Object.entries(values)) {
        if (typeof value !== "string") continue;
        const cssVariable =
            tokenToCssVariable[tokenId as keyof typeof tokenToCssVariable];
        if (cssVariable) {
            compiled[cssVariable] = value;
        }
    }
    return compiled;
}
